/**
 * The reports surface's own arithmetic and geometry (P7-T2).
 *
 * Everything here is pure and testable without a browser, and nothing here adds money: the figures
 * arrive from `/api/v1/reports` and `/api/v1/forecast` as canonical minor-unit strings computed by
 * `@saldo/domain`, and this module only *lays them out* — a label for the scope, the rows of the
 * forecast's breakdown, and the rectangle of a chart bar.
 *
 * Three of the plan's clauses are decided here rather than in the component, because they are
 * properties of the presentation and a reviewer should be able to read them:
 *
 *   - `barGeometry` never mixes two currencies: it is called once per block, so a chart has exactly
 *     one currency in it (`RPT-05`, `FX-04`);
 *   - `forecastBreakdown` always emits the incomplete-coverage row, so a projection is never drawn
 *     without its own caveat beside it (`RPT-04`);
 *   - `overdueRows` names the overdue items the forecast counted once, so "counted once" is
 *     visible as one row rather than inferred from a total (`REC-05`, F-29).
 */

import type {
  CategoryReportRowBody,
  CurrencyReportBlockBody,
  ForecastBody,
  IncomeExpenseReportBody,
  ReportsBody,
} from '@saldo/contracts';

/** The report scope the surface is showing, and the date its forecast runs to. */
export interface ReportScopeState {
  readonly kind: 'month' | 'range' | 'all';
  /** `YYYY-MM`, used when `kind` is `month`. */
  readonly month: string;
  /** Inclusive bounds, used when `kind` is `range`. */
  readonly from: string;
  readonly to: string;
  readonly role: 'all' | 'cash' | 'card';
  /** The selected forecast date, `YYYY-MM-DD`. */
  readonly date: string;
}

/** The starting scope: the business day's own month, every role, forecasting to the business day. */
export function initialScope(businessToday: string): ReportScopeState {
  return {
    kind: 'month',
    month: businessToday.slice(0, 7),
    from: `${businessToday.slice(0, 7)}-01`,
    to: businessToday,
    role: 'all',
    date: businessToday,
  };
}

/** The scope as a query string, with only the bounds the kind actually uses. */
export function reportsQuery(state: ReportScopeState): string {
  const parts = [`scope=${state.kind}`];
  if (state.kind === 'month') parts.push(`month=${state.month}`);
  if (state.kind === 'range') parts.push(`from=${state.from}`, `to=${state.to}`);
  if (state.role !== 'all') parts.push(`role=${state.role}`);
  return parts.join('&');
}

export function reportsPath(state: ReportScopeState): string {
  return `/api/v1/reports?${reportsQuery(state)}`;
}

export function forecastPath(accountId: string, state: ReportScopeState): string {
  return `/api/v1/forecast?accountId=${accountId}&through=${state.date}`;
}

/** What the surface says it is showing — the period's own label, or the role on its own. */
export function scopeDescription(state: ReportScopeState): string {
  if (state.kind === 'month') return `Month ${state.month}`;
  if (state.kind === 'range') return `${state.from} to ${state.to}`;
  return 'All periods';
}

export type BarKind = 'income' | 'expense' | 'net' | 'category';

export interface ChartBar {
  readonly label: string;
  readonly minor: bigint;
  readonly kind: BarKind;
  readonly testId: string;
}

export interface ChartGeometry {
  readonly width: number;
  readonly height: number;
  readonly maxMinor: bigint;
  readonly bars: readonly {
    readonly testId: string;
    readonly label: string;
    readonly kind: BarKind;
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    readonly magnitudeMinor: bigint;
  }[];
}

/**
 * The summary chart's bars for one currency: income, expenses and the net between them.
 *
 * Expense and net are drawn as magnitudes with their sign in the label and the class, so a bar's
 * height is comparable across the three and no bar is a direction the eye has to infer.
 */
export function summaryBars(totals: IncomeExpenseReportBody): readonly ChartBar[] {
  return [
    {
      label: 'Income',
      minor: BigInt(totals.incomeMinor),
      kind: 'income',
      testId: 'chart-bar-income',
    },
    {
      label: 'Expenses',
      minor: BigInt(totals.expenseMinor),
      kind: 'expense',
      testId: 'chart-bar-expense',
    },
    { label: 'Net', minor: BigInt(totals.netMinor), kind: 'net', testId: 'chart-bar-net' },
  ];
}

/** The category chart's bars: every category's expense magnitude, largest first, bounded. */
export function categoryBars(
  block: CurrencyReportBlockBody,
  limit = 8,
): readonly ChartBar[] {
  return [...block.categories]
    .filter((row) => BigInt(row.totals.expenseMinor) > 0n || BigInt(row.totals.incomeMinor) > 0n)
    .sort((left, right) => {
      const leftMagnitude = magnitude(BigInt(left.totals.expenseMinor)) + magnitude(BigInt(left.totals.incomeMinor));
      const rightMagnitude = magnitude(BigInt(right.totals.expenseMinor)) + magnitude(BigInt(right.totals.incomeMinor));
      return rightMagnitude > leftMagnitude ? 1 : rightMagnitude < leftMagnitude ? -1 : left.label.localeCompare(right.label);
    })
    .slice(0, limit)
    .map((row) => ({
      label: row.label,
      minor: magnitude(BigInt(row.totals.expenseMinor)),
      kind: 'category' as const,
      testId: 'chart-bar-category',
    }));
}

function magnitude(minor: bigint): bigint {
  return minor < 0n ? -minor : minor;
}

/**
 * Lay bars out inside a box.
 *
 * The tallest bar fills the box and the shortest is proportional to it, so the geometry is a
 * monotone function of the amount — which is the property a chart owes a reader, and the one a
 * test can hold it to.
 */
export function barGeometry(
  bars: readonly ChartBar[],
  options: { readonly width?: number; readonly height?: number; readonly gap?: number } = {},
): ChartGeometry {
  const width = options.width ?? 320;
  const height = options.height ?? 120;
  const gap = options.gap ?? 8;
  const maxMinor = bars.reduce((max, bar) => (magnitude(bar.minor) > max ? magnitude(bar.minor) : max), 0n);
  const count = bars.length;
  const barWidth = count === 0 ? 0 : Math.max(1, Math.floor((width - gap * (count - 1)) / count));
  const laid = bars.map((bar, index) => {
    const barMagnitude = magnitude(bar.minor);
    const barHeight = maxMinor === 0n ? 0 : Number((barMagnitude * BigInt(height)) / maxMinor);
    return {
      testId: bar.testId,
      label: bar.label,
      kind: bar.kind,
      x: index * (barWidth + gap),
      y: height - barHeight,
      width: barWidth,
      height: barHeight,
      magnitudeMinor: barMagnitude,
    };
  });
  return { width, height, maxMinor, bars: laid };
}

export interface BreakdownRow {
  readonly testId: string;
  readonly label: string;
  /** The signed amount as a canonical minor string, ready for `formatBalance`. */
  readonly minor: string;
  readonly note: string;
}

/**
 * The forecast's expandable breakdown, in the order the plan words it: recorded balance, pending
 * outgoing, expected incoming and the explicit planned transfers — plus the overdue subset, which is
 * shown as its own row *because it is already inside the rows above it* and must not be added again
 * (`REC-05`, F-29).
 */
export function forecastBreakdown(forecast: ForecastBody): readonly BreakdownRow[] {
  const components = forecast.components;
  const value = (name: keyof typeof components): string => String(components[name]);
  return [
    {
      testId: 'forecast-row-recorded',
      label: 'Recorded balance',
      minor: forecast.recordedMinor,
      note: `settled at ${forecast.cutoff}`,
    },
    {
      testId: 'forecast-row-pending-outgoing',
      label: 'Pending outgoing',
      minor: negate(value('pendingOutgoingMinor')),
      note: `${value('pendingCount')} row(s) not settled by the cutoff`,
    },
    {
      testId: 'forecast-row-pending-incoming',
      label: 'Expected incoming',
      minor: value('pendingIncomingMinor'),
      note: 'not yet received',
    },
    {
      testId: 'forecast-row-planned-outgoing',
      label: 'Planned transfers out',
      minor: negate(value('plannedOutgoingMinor')),
      note: `${value('plannedCount')} explicit transfer leg(s)`,
    },
    {
      testId: 'forecast-row-planned-incoming',
      label: 'Planned transfers in',
      minor: value('plannedIncomingMinor'),
      note: 'explicit, never a derived card bill',
    },
    {
      testId: 'forecast-row-future-completed',
      label: 'Recorded, dated after the cutoff',
      minor: subtract(value('futureCompletedIncomingMinor'), value('futureCompletedOutgoingMinor')),
      note: 'completed transfers whose own date has not arrived',
    },
    {
      testId: 'forecast-row-projected',
      label: `Projected ${forecast.through}`,
      minor: forecast.projectedMinor,
      note: 'recorded plus everything above',
    },
  ];
}

/** The overdue rows the projection already counted once. Shown, never added. */
export function overdueRows(forecast: ForecastBody): readonly BreakdownRow[] {
  const components = forecast.components;
  const count = Number(components.overdueCount);
  if (count === 0) return [];
  return [
    {
      testId: 'forecast-row-overdue-outgoing',
      label: 'Of which overdue outgoing',
      minor: negate(String(components.overdueOutgoingMinor)),
      note: 'already counted above, once',
    },
    {
      testId: 'forecast-row-overdue-incoming',
      label: 'Of which overdue incoming',
      minor: String(components.overdueIncomingMinor),
      note: 'still unreceived',
    },
  ];
}

/**
 * The forecast's own caveat, in words.
 *
 * The statement has three states and the renderer names which one it is, because the horizon alone
 * is ambiguous: "complete through 2026-10-31" reads as a claim about the whole horizon, and when the
 * generation has a gap that day is precisely the point it stops being complete (`F-08`: a forecast
 * says *incomplete* and identifies its boundary, and never presents ungenerated commitments as
 * zero). `unknown` — a template that has produced nothing — names no day even though it covers none.
 *
 * The note itself is the API's own text; this function adds no judgement of its own. A reader who
 * sees a projection sees this beside it (`RPT-04`).
 */
export function coverageText(forecast: ForecastBody): string {
  const { complete, completeThrough, note } = forecast.coverage;
  const state = complete ? 'generation complete through' : 'generation incomplete through';
  const horizon = completeThrough === null ? 'no day yet' : completeThrough;
  return `${state}: ${horizon} — ${note}`;
}

/** Whether the surface should mark the scope as carrying money that is already late. */
export function hasOverdue(forecast: ForecastBody): boolean {
  const components = forecast.components;
  return (
    Number(components.overdueCount) > 0 ||
    BigInt(components.overdueOutgoingMinor) > 0n ||
    BigInt(components.overdueIncomingMinor) > 0n
  );
}

/** A category row's own label, with the two properties that change how it must be read. */
export function categoryLabel(row: CategoryReportRowBody): string {
  if (row.categoryId === null) return `${row.label} (uncategorized)`;
  return row.archived ? `${row.label} (archived)` : row.label;
}

/** The period label the API named, so a heading can never outrun the data. */
export function periodLabel(body: ReportsBody): string {
  return body.period.label;
}

function negate(minor: string): string {
  const value = BigInt(minor);
  return (value === 0n ? 0n : -value).toString();
}

function subtract(left: string, right: string): string {
  return (BigInt(left) - BigInt(right)).toString();
}
